import { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { db } from '../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { appId } from '../constants';
import { Utensils, Martini, Coffee, Binoculars, Music, MapPin, ExternalLink, X } from 'lucide-react';
import { renderToString } from 'react-dom/server';

// Fix for default marker icons in Leaflet + React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const MapResizer = () => {
  const map = useMap();
  useEffect(() => {
    map.invalidateSize();
  }, [map]);
  return null;
};

const getCategoryIcon = (category) => {
  switch (category) {
    case 'food': return <Utensils size={14} />;
    case 'drinks': return <Martini size={14} />;
    case 'coffee': return <Coffee size={14} />;
    case 'sights': return <Binoculars size={14} />;
    case 'concerts': return <Music size={14} />;
    default: return <MapPin size={14} />;
  }
};

const getCategoryColor = (category) => {
  switch (category) {
    case 'food': return '#f97316'; // orange-500
    case 'drinks': return '#8b5cf6'; // violet-500
    case 'coffee': return '#a16207'; // yellow-700
    case 'sights': return '#06b6d4'; // cyan-500
    case 'concerts': return '#ec4899'; // pink-500
    default: return '#3b82f6'; // blue-500
  }
};

const createCustomIcon = (category) => {
  const color = getCategoryColor(category);
  const iconHtml = renderToString(
    <div style={{
      backgroundColor: color,
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      border: '2px solid white',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
    }}>
      {getCategoryIcon(category)}
    </div>
  );

  return L.divIcon({
    html: iconHtml,
    className: 'custom-leaflet-icon',
    iconSize: [28, 28],
    iconAnchor: [14, 14]
  });
};

const CATEGORIES = [
  { id: 'food', label: 'Food', icon: Utensils, tags: ['restaurant', 'food', 'pizza', 'burger', 'sushi', 'mexican', 'italian', 'steak'] },
  { id: 'drinks', label: 'Drinks', icon: Martini, tags: ['bar', 'drinks', 'cocktails', 'wine', 'beer', 'pub'] },
  { id: 'coffee', label: 'Coffee', icon: Coffee, tags: ['coffee', 'cafe', 'tea', 'bakery'] },
  { id: 'sights', label: 'Sights', icon: Binoculars, tags: ['sight', 'museum', 'park', 'attraction', 'view', 'hike'] },
  { id: 'concerts', label: 'Concerts', icon: Music, tags: ['concert', 'music', 'show', 'festival', 'venue'] },
];

export default function MobileSocialMap({ allUsers }) {
  const [activeCategory, setActiveCategory] = useState('food');
  const [recommendations, setRecommendations] = useState([]);
  const [selectedPin, setSelectedPin] = useState(null);

  useEffect(() => {
    // Fetch all recommendations globally
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'events'),
      where('isRecommendation', '==', true)
    );
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map(d => ({ ...d.data(), id: d.id }));
      // Filter out items without coordinates
      const valid = docs.filter(d => (d.lat && d.lng) || (d.addressLat && d.addressLng));
      setRecommendations(valid);
    });
    return () => unsub();
  }, []);

  const filteredPins = useMemo(() => {
    const categoryDef = CATEGORIES.find(c => c.id === activeCategory);
    if (!categoryDef) return [];

    return recommendations.filter(rec => {
      // Very basic categorization based on tags or title for now
      // In the future, this should rely on a rigid taxonomy
      const lowerTags = (rec.tags || []).map(t => t.toLowerCase());
      const lowerTitle = (rec.title || '').toLowerCase();
      
      return categoryDef.tags.some(t => 
        lowerTags.includes(t) || lowerTitle.includes(t)
      );
    });
  }, [recommendations, activeCategory]);

  return (
    <div className="relative flex-1 w-full h-full flex flex-col">
      <div className="flex-1 w-full z-0 relative">
        <MapContainer
          center={[39.8283, -98.5795]} // Center of US as fallback
          zoom={4}
          style={{ height: '100%', width: '100%', zIndex: 0 }}
          zoomControl={false}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
            attribution='&copy; OpenStreetMap &copy; CARTO'
          />
          <MapResizer />
          
          {filteredPins.map(pin => {
            const lat = pin.addressLat || pin.lat;
            const lng = pin.addressLng || pin.lng;
            return (
              <Marker
                key={pin.id}
                position={[parseFloat(lat), parseFloat(lng)]}
                icon={createCustomIcon(activeCategory)}
                eventHandlers={{
                  click: () => setSelectedPin(pin)
                }}
              />
            );
          })}
        </MapContainer>
      </div>

      {/* Selected Pin Overlay Card */}
      {selectedPin && (
        <div className="absolute bottom-20 left-4 right-4 bg-white rounded-2xl shadow-2xl z-20 overflow-hidden border border-zinc-100 flex flex-col max-h-[40vh]">
          <div className="p-4 flex flex-col gap-2">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-black text-zinc-900 leading-tight">{selectedPin.venueName || selectedPin.title}</h3>
                {selectedPin.address && (
                  <p className="text-xs font-medium text-zinc-500 mt-0.5">{selectedPin.address}</p>
                )}
              </div>
              <button 
                onClick={() => setSelectedPin(null)}
                className="p-1 bg-zinc-100 rounded-full text-zinc-500 shrink-0 ml-2"
              >
                <X size={16} />
              </button>
            </div>
            
            {(selectedPin.tags && selectedPin.tags.length > 0) && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {selectedPin.tags.map(tag => (
                  <span key={tag} className="bg-zinc-100 text-zinc-600 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            <a 
              href={`https://www.google.com/maps/search/?api=1&query=${selectedPin.addressLat || selectedPin.lat},${selectedPin.addressLng || selectedPin.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 w-full bg-blue-500 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 text-sm shadow-sm active:bg-blue-600 transition-colors"
            >
              <ExternalLink size={16} /> Open in Google Maps
            </a>
          </div>
        </div>
      )}

      {/* Category Toolbar */}
      <div className="absolute bottom-4 left-4 right-4 bg-white rounded-2xl shadow-xl z-10 border border-zinc-100 overflow-x-auto custom-scrollbar">
        <div className="flex items-center gap-1 p-1.5 min-w-max">
          {CATEGORIES.map(cat => {
            const Icon = cat.icon;
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => {
                  setActiveCategory(cat.id);
                  setSelectedPin(null); // Clear selected pin when changing category
                }}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                  isActive ? 'bg-zinc-900 text-white shadow-md' : 'text-zinc-500 hover:bg-zinc-100'
                }`}
              >
                <Icon size={14} strokeWidth={isActive ? 3 : 2} />
                {cat.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
